import { range, uniqBy, flatMap } from 'lodash';

type GetPos = (start: number, length: number, total: number) => number;

type GetDimensions = (
  length: number,
  current: number,
  total: number,
) => [number, number, number];

const mapper = n => [n, n.toString()] as [number, string];

const getEnd: GetPos = (start, length, total) => {
  if (total < start + length) {
    return total + 1;
  }

  return start + length;
};

const getStart: GetPos = (current, length, total) => {
  const min = 1;

  let start = current - Math.floor(length / 2);
  start = Math.max(start, min);
  start = Math.min(start, min + total - length);
  return start;
};

const getDimensions: GetDimensions = (rangeLength, current, total) => {
  const length = Math.min(rangeLength, total);
  const start = getStart(current, length, total);
  const end = getEnd(start, length, total);

  return [length, start, end];
};

type GetPagingRange = (
  current: number,
  total: number,
  rangeLength?: number,
  withEllipsis?: boolean,
) => [number, string][];

const getPagingRangePlain: GetPagingRange = (current, total, rangeLength) => {
  const [_, start, end] = getDimensions(rangeLength || 5, current, total);
  return range(start, end, 1).map(mapper);
};

const getPagingRangeWithEllipsis: GetPagingRange = (
  current,
  total,
  rangeLength,
) => {
  const [length, start, end] = getDimensions(rangeLength || 3, current, total);
  const ellipsis = start > length + 1 && total - length > length;
  const extraEllipsis = end - 1 < total - length;

  return uniqBy(
    flatMap([
      range(1, length + 1, 1).map(mapper),
      ellipsis
        ? [[Math.min(start - 1, total - length), '...'] as [number, string]]
        : [],
      range(start, end, 1).map(mapper),
      extraEllipsis ? [[end, '...'] as [number, string]] : [],
      range(Math.max(total - length + 1, 1), total + 1, 1).map(mapper),
    ]),
    ([value]) => value,
  );
};

export const getPagingRange: GetPagingRange = (
  current,
  total,
  rangeLength,
  withEllipsis,
) => {
  if (withEllipsis) {
    return getPagingRangeWithEllipsis(current, total, rangeLength);
  }
  return getPagingRangePlain(current, total, rangeLength);
};
